// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t529 · B-2267 · LA GRABADORA DE LA CONSULTA GRABADA (una por pestaña; función «consulta-grabada», 28-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  Vive FUERA de React, a propósito: la consulta se graba mientras el doctor abre la ficha, el historial o la agenda, y una
//  grabadora dentro de un componente se pararía al cambiar de pantalla. La pinta components/ConsultaGrabada.jsx
//  (IndicadorConsultaGrabada, montado una vez en App.jsx): el punto rojo y el tiempo, siempre a la vista.
//
//  CÓMO GRABA (lo de las notas de voz, utils/grabacion, más lo que pide una consulta entera):
//   · el micrófono que el doctor ha elegido en ESTE ordenador (puede ser uno Bluetooth); si no está, el de siempre, y se avisa;
//   · un TROZO cada 5 segundos, que se manda en el acto por la cola del dispositivo (utils/colaEnvios): si se corta la red o
//     el servidor no contesta, se guarda en el dispositivo y se manda solo, sin duplicarse (el servidor lo reconoce);
//   · un SEGMENTO nuevo cada 5 minutos grabados: cada segmento es un archivo entero, que el servidor pasa a texto por separado;
//   · pausa y reanudar en el mismo segmento; hasta 60 minutos (a los 60 se para sola);
//   · la pantalla no se apaga mientras graba (al apagarse, el móvil corta el micrófono).
//  Primero el micrófono y DESPUÉS el servidor: si no hay micrófono o se niega el permiso, no se apunta ninguna grabación.
import api from '../services/api'
import { enviarConCola } from './colaEnvios'
import { puedeGrabar, puedePausarGrabacion, elegirMimeGrabacion, extensionDeNota } from './grabacion'
import { t as i18nT } from './i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

export const TROZO_MS = 5000
export const CLAVE_MICROFONO = 'biango.consultaGrabada.microfono'
export const EVENTO_CAMBIO = 'biango:consulta-grabada-cambio'

// La ventana, el navegador y el almacén SIEMPRE por `window` (en el navegador es lo mismo; así se puede medir con una ventana de mentira).
const W = () => (typeof window !== 'undefined' && window ? window : {})
const PARADA = Object.freeze({ estado: 'parada', consultaId: null, citaId: null, paciente: null, segundos: 0, pausada: false, error: null, aviso: null, ultima: null, maxSegundos: 3600 })
let st = { ...PARADA }
const oyentes = new Set()
let mr = null, stream = null, timer = null, wake = null
let seg = 0, segSeg = 0, segmentoSeg = 300, conteo = {}, ext = 'webm', mime = ''
let rotando = false, parando = false
const pendientes = new Set()

function emitir(cambio) {
  st = { ...st, ...cambio }
  for (const f of [...oyentes]) { try { f(st) } catch (_) { /* quien escucha no tumba a los demás */ } }
}
function avisarCambio() {
  try { const w = W(); w.dispatchEvent(new w.CustomEvent(EVENTO_CAMBIO, { detail: { ...st } })) } catch (_) { /* sin ventana */ }
}
export function suscribir(f) { oyentes.add(f); try { f(st) } catch (_) { /* nada */ } return () => { oyentes.delete(f) } }
export function estadoGrabadora() { return st }
export function grabandoAhora() { return st.estado === 'grabando' || st.estado === 'arrancando' || st.estado === 'parando' }

// ── EL MICRÓFONO DE ESTE ORDENADOR ─────────────────────────────────────────────────────────────────────────────
export function microfonoGuardado() {
  try { const v = JSON.parse(W().localStorage.getItem(CLAVE_MICROFONO) || 'null'); return v && v.deviceId ? v : null } catch (_) { return null }
}
export function guardarMicrofono(m) {
  try { const ls = W().localStorage; if (m && m.deviceId) ls.setItem(CLAVE_MICROFONO, JSON.stringify({ deviceId: String(m.deviceId), label: String(m.label || '') })); else ls.removeItem(CLAVE_MICROFONO) } catch (_) { /* sin almacenamiento: el de siempre */ }
}
// Los micrófonos de este ordenador. Sin permiso, el navegador no da sus nombres: `conNombres` lo dice.
export async function listarMicrofonos({ pedirPermiso = false } = {}) {
  const md = W().navigator ? W().navigator.mediaDevices : null
  if (!md || typeof md.enumerateDevices !== 'function') return { lista: [], conNombres: false, soporta: false }
  if (pedirPermiso && typeof md.getUserMedia === 'function') {
    try { const s = await md.getUserMedia({ audio: true }); s.getTracks().forEach((t) => t.stop()) } catch (_) { /* sin permiso: sin nombres */ }
  }
  const ds = await md.enumerateDevices().catch(() => [])
  const lista = ds.filter((d) => d.kind === 'audioinput').map((d) => ({ deviceId: d.deviceId, label: d.label || '' }))
  return { lista, conNombres: lista.some((d) => d.label), soporta: true }
}
// El micrófono elegido, si está; si no, el de siempre y el aviso.
async function abrirMicrofono() {
  const md = W().navigator.mediaDevices
  const elegido = microfonoGuardado()
  let aviso = null
  if (elegido) {
    let esta = true
    try {
      const ds = await md.enumerateDevices()
      const ins = ds.filter((d) => d.kind === 'audioinput')
      if (ins.some((d) => d.deviceId)) esta = ins.some((d) => d.deviceId === elegido.deviceId)
    } catch (_) { esta = true }
    if (esta) {
      try { return { stream: await md.getUserMedia({ audio: { deviceId: { exact: elegido.deviceId } } }), aviso } } catch (_) { /* no está o no se deja: el de siempre */ }
    }
    aviso = 'El micrófono elegido' + (elegido.label ? ' («' + elegido.label + '»)' : '') + ' no está: se graba con el de siempre.'
  }
  return { stream: await md.getUserMedia({ audio: true }), aviso }
}

// ── LA PANTALLA DESPIERTA Y NO CERRAR SIN QUERER ──────────────────────────────────────────────────────────────
async function pedirPantalla() {
  try { const nav = W().navigator, doc = W().document; if (!wake && nav && nav.wakeLock && doc && doc.visibilityState === 'visible') { wake = await nav.wakeLock.request('screen'); wake.addEventListener('release', () => { wake = null }) } } catch (_) { /* sin permiso: sigue */ }
}
const alVolver = () => { const doc = W().document; if (doc && doc.visibilityState === 'visible' && st.estado === 'grabando') pedirPantalla() }
const antesDeCerrar = (e) => { if (st.estado === 'grabando' || st.estado === 'parando') { e.preventDefault(); e.returnValue = '' } }
function engancharVentana(si) {
  try {
    const w = W(), doc = w.document
    if (si) { doc && doc.addEventListener('visibilitychange', alVolver); w.addEventListener && w.addEventListener('beforeunload', antesDeCerrar) }
    else { doc && doc.removeEventListener('visibilitychange', alVolver); w.removeEventListener && w.removeEventListener('beforeunload', antesDeCerrar) }
  } catch (_) { /* sin ventana */ }
}
function soltarMicrofono() {
  clearInterval(timer); timer = null
  try { stream && stream.getTracks().forEach((t) => t.stop()) } catch (_) { /* ya estaba */ }
  stream = null
  try { wake && wake.release() } catch (_) { /* ya estaba */ }
  wake = null
  engancharVentana(false)
}

// ── LOS TROZOS ─────────────────────────────────────────────────────────────────────────────────────────────────
function mandarTrozo(id, s, k, blob) {
  conteo[s] = Math.max(conteo[s] || 0, k + 1)
  const url = '/consulta-grabada/' + id + '/trozos'
  const p = (async () => {
    const r = await enviarConCola({ tipo: 'consulta_trozo', metodo: 'POST', url, archivo: { campo: 'audio', blob, nombre: 'trozo.' + ext },
      campos: { segmento: String(s), n: String(k) }, etiqueta: 'consulta grabada' })
    if (r && r.sinCola) {
      const fd = new FormData()
      fd.append('segmento', String(s)); fd.append('n', String(k)); fd.append('audio', blob, 'trozo.' + ext)
      try { await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } }) } catch (e) {
        if (e && e.response && e.response.status >= 400 && e.response.status < 500) fallar((e.response.data && e.response.data.error) || 'El servidor no ha aceptado la grabación.')
      }
      return
    }
    if (r && (r.ok || r.pendiente)) return
    fallar((r && r.error) || 'El servidor no ha aceptado la grabación.')
  })()
  pendientes.add(p)
  p.finally(() => pendientes.delete(p))
}
function fallar(msg) {
  if (st.estado === 'parada') return
  const id = st.consultaId
  try { if (mr) { mr.ondataavailable = null; mr.onstop = null; if (mr.state !== 'inactive') mr.stop() } } catch (_) { /* ya estaba */ }
  mr = null
  soltarMicrofono()
  emitir({ ...PARADA, error: 'La grabación se ha parado: ' + msg, ultima: id ? { id, citaId: st.citaId } : null })
  avisarCambio()
}

function nuevoRecorder(id) {
  const r = new (W().MediaRecorder)(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: 32000 })
  const miSeg = seg
  let k = 0
  r.ondataavailable = (e) => { if (e.data && e.data.size) mandarTrozo(id, miSeg, k++, e.data) }
  r.onstop = () => {
    if (rotando) {
      rotando = false
      seg += 1; segSeg = 0
      try { mr = nuevoRecorder(id); mr.start(TROZO_MS) } catch (_) { fallar('no se ha podido seguir grabando.') }
    } else if (parando) {
      terminarDeParar(id)
    }
  }
  return r
}

function tic() {
  if (st.estado !== 'grabando' || st.pausada || !mr) return
  const s = st.segundos + 1
  segSeg += 1
  emitir({ segundos: s })
  if (s >= st.maxSegundos) { parar({ aviso: i18nT('utils.consultaGrabadaGrabadora.se_ha_llegado_a_los', 'Se ha llegado a los 60 minutos: la grabación se ha parado sola.') }); return }
  if (segSeg >= segmentoSeg && mr.state === 'recording') { rotando = true; try { mr.stop() } catch (_) { rotando = false } }
}

// ── EMPEZAR, PAUSAR, PARAR, DESCARTAR ──────────────────────────────────────────────────────────────────────────
export function puedeGrabarAqui() { return puedeGrabar(W()) }
export function puedePausarAqui() { return puedePausarGrabacion(W()) }

export async function empezar({ citaId }) {
  if (st.estado !== 'parada') return { ok: false, error: i18nT('utils.consultaGrabadaGrabadora.ya_hay_una_grabacion_en', 'Ya hay una grabación en marcha.') }
  if (!puedeGrabarAqui()) { emitir({ error: 'Este navegador no permite grabar audio.' }); return { ok: false } }
  emitir({ ...PARADA, estado: 'arrancando' })
  let abierto
  try { abierto = await abrirMicrofono() } catch (_) {
    emitir({ ...PARADA, error: 'No se ha podido usar el micrófono (permiso denegado o no hay ninguno).' })
    return { ok: false }
  }
  let r
  try { r = (await api.post('/consulta-grabada/empezar', { cita_id: citaId })).data } catch (e) {
    try { abierto.stream.getTracks().forEach((t) => t.stop()) } catch (_) { /* nada */ }
    emitir({ ...PARADA, error: (e && e.response && e.response.data && e.response.data.error) || 'No se ha podido empezar a grabar.' })
    return { ok: false }
  }
  stream = abierto.stream
  mime = elegirMimeGrabacion(W())
  ext = extensionDeNota(mime)
  seg = 0; segSeg = 0; conteo = {}; rotando = false; parando = false
  segmentoSeg = Number(r.segmento_segundos) > 0 ? Number(r.segmento_segundos) : 300
  try { mr = nuevoRecorder(r.consulta_id); mr.start(TROZO_MS) } catch (_) {
    soltarMicrofono()
    try { await api.post('/consulta-grabada/' + r.consulta_id + '/descartar') } catch (_) { /* la purga la cierra */ }
    emitir({ ...PARADA, error: 'Este navegador no ha podido empezar a grabar.' })
    return { ok: false }
  }
  timer = setInterval(tic, 1000)
  engancharVentana(true)
  pedirPantalla()
  emitir({ estado: 'grabando', consultaId: r.consulta_id, citaId: r.cita_id, paciente: r.paciente || null, segundos: 0, pausada: false,
    error: null, aviso: abierto.aviso, avisoDoctor: r.aviso_doctor || null, maxSegundos: Number(r.max_segundos) || 3600, ultima: null })
  avisarCambio()
  return { ok: true, consultaId: r.consulta_id }
}

export function pausar() {
  if (!mr || st.estado !== 'grabando' || st.pausada || mr.state !== 'recording') return false
  try { mr.pause() } catch (_) { return false }
  if (mr.state !== 'paused') return false
  emitir({ pausada: true })
  return true
}
export function reanudar() {
  if (!mr || st.estado !== 'grabando' || !st.pausada) return false
  try { mr.resume() } catch (_) { return false }
  if (mr.state !== 'recording') return false
  emitir({ pausada: false })
  pedirPantalla()
  return true
}

export function parar({ aviso = null } = {}) {
  if (!mr || st.estado !== 'grabando') return
  parando = true
  clearInterval(timer); timer = null
  emitir({ estado: 'parando', pausada: false, ...(aviso ? { aviso } : {}) })
  try { if (mr.state === 'paused') mr.resume() } catch (_) { /* sigue */ }
  try { mr.stop() } catch (_) { terminarDeParar(st.consultaId) }
}
async function terminarDeParar(id) {
  parando = false
  mr = null
  soltarMicrofono()
  // Lo que todavía va de camino se espera un poco; lo que no llegue, lo manda la cola del dispositivo (el servidor lo espera).
  await Promise.race([Promise.allSettled([...pendientes]), new Promise((ok) => setTimeout(ok, 15000))])
  let error = null
  try { await api.post('/consulta-grabada/' + id + '/parar', { duracion_seg: st.segundos, trozos: conteo }) } catch (e) {
    error = (e && e.response && e.response.data && e.response.data.error) || 'No se ha podido avisar al servidor de que la grabación ha terminado: se cerrará sola.'
  }
  emitir({ ...PARADA, error, aviso: st.aviso, ultima: { id, citaId: st.citaId, segundos: st.segundos } })
  avisarCambio()
}

export async function descartar() {
  const id = st.consultaId
  if (!id) return
  try { if (mr) { mr.ondataavailable = null; mr.onstop = null; if (mr.state !== 'inactive') mr.stop() } } catch (_) { /* ya estaba */ }
  mr = null
  soltarMicrofono()
  emitir({ ...PARADA })
  try { await api.post('/consulta-grabada/' + id + '/descartar') } catch (_) { /* la purga la cierra y borra lo que llegó */ }
  avisarCambio()
}

export function olvidarUltima() { emitir({ ultima: null, error: null, aviso: null }) }

